import { paths } from 'src/routes/paths';

/**
 * URL-segmentet som identifierar demo-organisationen mot backend
 * (`POST /api/demo/{profil}`). Måste matcha `Demoprofil.vägsegment` i backend, och är
 * ren ASCII eftersom den går rakt in i URL:en.
 */
export type DemoProfil = 'bostadsrattsforening' | 'ekonomisk-forening' | 'aktiebolag';

export type DemoSteg = {
  id: string;
  titel: string;
  ikon: string;
  path: string;
};

export type DemoOrganisation = {
  profil: DemoProfil;
  /** Organisationsnumret demon får. Saknas för profiler som ännu inte finns i backend. */
  organisationsNummer?: string;
  title: string;
  subtitle: string;
  badge: string;
  badgeColor: 'success' | 'warning' | 'info' | 'default';
  icon: string;
  /** Sant när backend faktiskt kan provisionera profilen. Annars är kortet en förhandsvisning. */
  aktiv: boolean;
  /** Vad demon innehåller. Håll listan ärlig — varje rad ska motsvara data som faktiskt seedas. */
  features: string[];
  /** Snabbvalen i guidefältet när demon är igång. */
  steg: DemoSteg[];
};

export const DEMO_ORGANISATIONER: DemoOrganisation[] = [
  {
    profil: 'bostadsrattsforening',
    organisationsNummer: '769600-1234',
    title: 'Bostadsrättsförening',
    subtitle: 'BRF Valnöten 4 (Stockholm Valnöten 4)',
    badge: 'Redo att testa',
    badgeColor: 'success',
    icon: 'solar:home-smile-bold-duotone',
    aktiv: true,
    features: [
      '40 lägenheter (38 bostadsrätter + 2 hyresrätter) fördelade på 2 fastigheter',
      'Medlemsregister med språkpreferenser (ES, AR, PL, FR, SV)',
      'Flerspråkig kommunikation & styrelseutskick',
      'Månadsavier, hyresfakturor & lokalhyror',
      '2 fastigheters fjärrvärmecentraler i realtid & 36 månaders historik',
      'Leverantörsfakturor (Ellevio, Nordea, Bredband2, Försäkring)',
      'Bilpool med 2 elbilar & Lådcykelpool med 4 el-lådcyklar',
      'Garage med 16 platser, laddstolpar & köhantering',
      'Utlåningspool med inventarier & bokningar',
      'Bokföring, Bokslut & Årsredovisning (Web & PDF)',
      'Fastighetslån & Låneportfölj (Bankintegration, Räntor & Avier)',
    ],
    steg: [
      { id: 'fastighet', titel: 'Fastighet & Info', ikon: 'solar:home-smile-bold-duotone', path: paths.dashboard.fastighet.minaFastigheter },
      { id: 'medlemmar', titel: 'Medlemsregister (40 lgh)', ikon: 'solar:users-group-rounded-bold-duotone', path: paths.dashboard.medlemmar.v2 },
      { id: 'kommunikation', titel: 'Kanaler & Utskick', ikon: 'solar:chat-round-dots-bold-duotone', path: paths.dashboard.boende.kanaler },
      { id: 'utlåningspool', titel: 'Utlåningspool', ikon: 'solar:box-minimalistic-bold-duotone', path: paths.dashboard.boende.utlåningspool.root },
      { id: 'garage', titel: 'Garage & Laddplatser', ikon: 'solar:wheel-bold-duotone', path: paths.dashboard.boende.garage },
      { id: 'bilpool', titel: 'Bilpool (2 elbilar)', ikon: 'solar:electric-refueling-bold-duotone', path: paths.dashboard.boende.bilpool },
      { id: 'ladcykelpool', titel: 'Lådcykelpool (4 cyklar)', ikon: 'solar:bicycling-bold-duotone', path: paths.dashboard.boende.lådcykelpool },
      { id: 'värme', titel: 'Värme (2 centraler & 36 mån)', ikon: 'solar:flame-bold-duotone', path: paths.dashboard.fastighet.värme },
      { id: 'kundfakturor', titel: 'Avier & Hyror', ikon: 'solar:document-text-bold-duotone', path: paths.dashboard.ekonomi.fakturor.kundfakturor },
      { id: 'levfakturor', titel: 'Leverantörsfakturor', ikon: 'solar:bill-list-bold-duotone', path: paths.dashboard.ekonomi.bokforing.händelser.root },
      { id: 'bokföring', titel: 'Bokföring & Bokslut', ikon: 'solar:calculator-bold-duotone', path: paths.dashboard.ekonomi.bokforing.root },
      { id: 'årsredovisning', titel: 'Årsredovisning', ikon: 'solar:file-check-bold-duotone', path: paths.dashboard.ekonomi.arsredovisningar.senaste },
      { id: 'lån', titel: 'Fastighetslån', ikon: 'solar:banknote-bold-duotone', path: paths.dashboard.ekonomi.fastighetslan.root },
    ],
  },
  {
    profil: 'ekonomisk-forening',
    organisationsNummer: '769602-5670',
    title: 'Ekonomisk förening',
    subtitle: 'Sambruket Ekonomisk Förening (Hägersten)',
    badge: 'Redo att testa',
    badgeColor: 'success',
    icon: 'solar:users-group-two-rounded-bold-duotone',
    aktiv: true,
    features: [
      '28 medlemshushåll med krypterade personuppgifter & självbetjäningsradering',
      'Medlemsavgifter per hushåll med betalningsstatus, förfallodag & påminnelser',
      'Inbetalningar matchade automatiskt mot banktransaktioner, med avvikelser att granska',
      'Utlåningspool med 18 objekt på 3 förvaringsplatser & komplett lånehistorik',
      'Stadgar enligt lagen (2018:672) om ekonomiska föreningar',
      'Stadgeändring med stämmobeslut & röstsiffror',
      'Styrelsemöten, dagordning, protokoll & ordinarie föreningsstämma',
      'Kanaler & medlemskommunikation',
      'Bokföring med två räkenskapsår: grundbok, huvudbok, resultat- & balansräkning',
      'Budget & likviditetsprognos med föreningens egna konton',
      'Dokumentstruktur för föreningens avtal, protokoll och register',
    ],
    steg: [
      { id: 'medlemmar', titel: 'Medlemsregister (28 hushåll)', ikon: 'solar:users-group-rounded-bold-duotone', path: paths.dashboard.medlemmar.v2 },
      { id: 'avgifter', titel: 'Medlemsavgifter', ikon: 'solar:wallet-money-bold-duotone', path: paths.dashboard.medlemmar.avgifter },
      { id: 'utlåningspool', titel: 'Utlåningspool (18 objekt)', ikon: 'solar:box-minimalistic-bold-duotone', path: paths.dashboard.boende.utlåningspool.root },
      { id: 'kommunikation', titel: 'Kanaler', ikon: 'solar:chat-round-dots-bold-duotone', path: paths.dashboard.boende.kanaler },
      { id: 'stadgar', titel: 'Stadgar & stämmobeslut', ikon: 'solar:document-text-bold-duotone', path: paths.dashboard.organisation.stadgar },
      { id: 'styrelse', titel: 'Styrelsen & stämman', ikon: 'solar:users-group-two-rounded-bold-duotone', path: paths.dashboard.styrelse.arkiv.root },
      { id: 'grunddata', titel: 'Om föreningen', ikon: 'solar:info-circle-bold-duotone', path: paths.dashboard.organisation.grunddata },
      { id: 'dokument', titel: 'Dokument', ikon: 'solar:folder-with-files-bold-duotone', path: paths.dashboard.dokument.root },
      { id: 'grundbok', titel: 'Bokföring', ikon: 'solar:calculator-bold-duotone', path: paths.dashboard.ekonomi.bokforing.grundbok },
      { id: 'resultat', titel: 'Resultaträkning', ikon: 'solar:chart-2-bold-duotone', path: paths.dashboard.ekonomi.bokforing.resultaträkning },
      { id: 'budget', titel: 'Budget & prognos', ikon: 'solar:pie-chart-2-bold-duotone', path: paths.dashboard.ekonomi.budget.root },
    ],
  },
  {
    profil: 'aktiebolag',
    organisationsNummer: '556123-4567',
    title: 'Aktiebolag',
    subtitle: 'Konsult & Utveckling AB (Stockholm)',
    badge: 'Redo att testa',
    badgeColor: 'success',
    icon: 'solar:buildings-3-bold-duotone',
    aktiv: true,
    features: [
      'Konsultbolag med två anställda, momsregistrerat och arbetsgivare',
      'Två räkenskapsår bokföring: grundbok, huvudbok, resultat- & balansräkning',
      'Fakturerade uppdrag med utgående moms och avdragsgill ingående moms',
      'Löner med källskatt och arbetsgivaravgifter, månad för månad',
      'Arbetsgivardeklarationer (AGI) per löntagare och redovisningsperiod',
      'Skattekonto med inbetalningar, avdragen skatt, arbetsgivaravgift & moms',
      'Kvartalsvisa momsdeklarationer',
      'Budget & likviditetsprognos med bolagets egna konton',
      'Dokumentstruktur för avtal, personal och bokföring',
    ],
    steg: [
      { id: 'grundbok', titel: 'Bokföring', ikon: 'solar:calculator-bold-duotone', path: paths.dashboard.ekonomi.bokforing.grundbok },
      { id: 'resultat', titel: 'Resultaträkning', ikon: 'solar:chart-2-bold-duotone', path: paths.dashboard.ekonomi.bokforing.resultaträkning },
      { id: 'balans', titel: 'Balansräkning', ikon: 'solar:scale-bold-duotone', path: paths.dashboard.ekonomi.bokforing.balansräkning },
      { id: 'lon', titel: 'Löner', ikon: 'solar:wallet-money-bold-duotone', path: paths.dashboard.ekonomi.loner.root },
      { id: 'skatt', titel: 'Skattekonto', ikon: 'solar:bill-list-bold-duotone', path: paths.dashboard.ekonomi.skatter.root },
      { id: 'moms', titel: 'Momsdeklarationer', ikon: 'solar:bill-check-bold-duotone', path: paths.dashboard.ekonomi.moms.momsdeklarationer.root },
      { id: 'budget', titel: 'Budget & prognos', ikon: 'solar:pie-chart-2-bold-duotone', path: paths.dashboard.ekonomi.budget.root },
      { id: 'dokument', titel: 'Dokument', ikon: 'solar:folder-with-files-bold-duotone', path: paths.dashboard.dokument.root },
    ],
  },
];

/**
 * Slår upp demoprofilen för en organisation via organisationsnumret.
 *
 * <p>Matchar medvetet <em>bara</em> på organisationsnummer. Den tidigare varianten letade
 * också efter "Valnöten" eller "Demo" i föreningens namn, vilket slog till på vilken riktig
 * förening som helst som råkade heta så.
 */
export function hittaDemoOrganisation(
  organisationsNummer?: string | null
): DemoOrganisation | undefined {
  if (!organisationsNummer) return undefined;
  const normaliserat = organisationsNummer.trim();
  return DEMO_ORGANISATIONER.find(
    (demo) => demo.organisationsNummer !== undefined && demo.organisationsNummer === normaliserat
  );
}
